/**
 * Hangman Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SudokuErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum HangmanErrorType {
  INVALID_GUESS = 'INVALID_GUESS',
  ALREADY_GUESSED = 'ALREADY_GUESSED',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface HangmanError {
  type: HangmanErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class HangmanErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: HangmanError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: HangmanErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): HangmanError {
    const error: HangmanError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Hangman', additionalData: details as Record<string, unknown> },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: HangmanErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case HangmanErrorType.INVALID_GUESS:
      case HangmanErrorType.ALREADY_GUESSED:
      case HangmanErrorType.GAME_ENDED:
      case HangmanErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case HangmanErrorType.CORRUPTED_DATA:
      case HangmanErrorType.SAVE_ERROR:
      case HangmanErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): HangmanError {
    return this.handleError(
      HangmanErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidGuess(letter: string): HangmanError {
    return this.handleError(
      HangmanErrorType.INVALID_GUESS,
      `Invalid guess: "${letter}". Please enter a single letter A-Z.`,
      { letter }
    );
  }

  handleAlreadyGuessed(letter: string): HangmanError {
    return this.handleError(
      HangmanErrorType.ALREADY_GUESSED,
      `You already guessed "${letter}". Try a different letter.`,
      { letter },
      true
    );
  }

  handleCorruptedData(dataType: string, error: unknown): HangmanError {
    return this.handleError(
      HangmanErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: unknown): HangmanError {
    return this.handleError(
      HangmanErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: unknown): HangmanError {
    return this.handleError(
      HangmanErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: HangmanErrorType): boolean {
    switch (type) {
      case HangmanErrorType.INVALID_GUESS:
      case HangmanErrorType.ALREADY_GUESSED:
      case HangmanErrorType.GAME_ENDED:
      case HangmanErrorType.GAME_LOGIC:
      case HangmanErrorType.CORRUPTED_DATA:
      case HangmanErrorType.SAVE_ERROR:
      case HangmanErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: HangmanError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: HangmanError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Hangman Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): HangmanError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
